import { GooglePlacesLoader } from '@/lib/constants'
import { GooglePlacesAddress } from '@/lib/apis/types'
import { useQuery } from '@tanstack/react-query'

export function useGoogleServices() {
  return useQuery({
    queryKey: ['google-services'],
    queryFn: async () => {
      return await GooglePlacesLoader.importLibrary('places').then((result) => {
        const { AutocompleteService, PlacesService } =
          result as google.maps.PlacesLibrary
        const autocompleteService = new AutocompleteService()
        const placesService = new PlacesService(
          document.getElementById('gmap-hidden') as HTMLDivElement,
        )

        return { autocomplete: autocompleteService, places: placesService }
      })
    },
  })
}

export function usePlacesAutocompleteService(
  services: ReturnType<typeof useGoogleServices>,
  input: string,
) {
  return useQuery({
    queryKey: ['google-places-autocomplete', input],
    queryFn: async ({ queryKey }) => {
      const [_, input] = queryKey

      if (!input || input.trim().length < 3) return []

      // Remove the ', Australia' suffix for returned addresses
      return (
        await services.data!.autocomplete.getPlacePredictions({
          input,
          types: ['address'],
          componentRestrictions: { country: 'au' },
        })
      ).predictions.map((prediction) => ({
        placeId: prediction.place_id,
        description: prediction.description.replace(/, Australia/, ''),
      }))
    },
    enabled: !!services.data,
  })
}

export function usePlacesDetailsService(
  services: ReturnType<typeof useGoogleServices>,
  placeId: string,
) {
  return useQuery({
    queryKey: ['google-places-details', placeId],
    queryFn: async ({ queryKey }) => {
      const [_, placeId] = queryKey
      if (!placeId) return

      return await new Promise<GooglePlacesAddress>((resolve, reject) => {
        services.data!.places.getDetails(
          {
            placeId,
            fields: ['address_components'],
          },
          (result, status) => {
            if (status === 'OK') {
              let suburb = ''
              let state = ''
              let postcode = ''

              for (const component of result?.address_components ?? []) {
                if (component.types.includes('locality'))
                  suburb = component.short_name
                else if (
                  component.types.includes('administrative_area_level_1')
                )
                  state = component.short_name
                else if (component.types.includes('postal_code'))
                  postcode = component.short_name
              }

              resolve({ suburb, state, postcode })
            } else reject()
          },
        )
      })
    },
    enabled: !!services.data && !!placeId,
  })
}
